import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, Modal, Table } from "reactstrap";
import {
  headers,
  tableData,
  documentKey,
  PREOFFER_VERIFICATION_STAGE_ID,
} from "./constants";
import * as BackendHelper from "@workspace/common/src/helpers/backend_helper";
import { toast } from "react-toastify";
import FilePreview from "@workspace/common/src/Components/FilePreview/FilePreview";
import { fetchJobTimelineFormSubmission } from "@workspace/job/src/store/actions";
import {
  callURL,
  GET_DOCUMENT_BY_ENTITY_URL_WITHOUT_USER_FILTER,
  getPreOfferVerificationByJobIdAndCandidateId,
} from "@workspace/job/src/helpers/backend_helper";
import { FileHelper } from "@workspace/common";
import { PRE_OFFER_VERIFICATION_DOCUMENTS } from "@workspace/job/src/components/PreOfferVerificationComponents/constants";

function PreOfferDocuments({ candidateId, jobId }) {
  const dispatch = useDispatch();

  const [povId, setPovId] = useState(null);
  const [formSubmissionData, setFormSubmissionData] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewModal, setPreviewModal] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [otherUploadedFiles, setOtherUploadedFiles] = useState(null);

  useEffect(() => {
    if (jobId && candidateId) {
      getPreOfferVerificationByJobIdAndCandidateId(jobId, candidateId).then(
        (response) => {
          setPovId(response?.data?.id);
          setFormSubmissionData(
            response?.data?.preOfferVerificationSubmissionData
          );
        }
      );
    }
  }, [jobId, candidateId]);

  useEffect(() => {
    if (povId) {
      callURL(
        GET_DOCUMENT_BY_ENTITY_URL_WITHOUT_USER_FILTER(PRE_OFFER_VERIFICATION_DOCUMENTS, povId)
      ).then((response) => {
        setOtherUploadedFiles(response?.data);
      });
    }
  }, [povId]);

  useEffect(() => {
    dispatch(
      fetchJobTimelineFormSubmission({
        jobId: jobId,
        candidateId: candidateId,
        jobStageId: PREOFFER_VERIFICATION_STAGE_ID,
      })
    );
  }, [jobId, candidateId, dispatch]);

  useEffect(() => {
    if (formSubmissionData) {
      const bodyData = Object.fromEntries(
        Object.entries(formSubmissionData).filter(
          ([key]) => key !== "vendorNames"
        )
      );
      setFileData(bodyData);
    }
  }, [formSubmissionData]);

  const generateBodyJsx = () => {
    const downloadCV = async (documentKey, povId, entityType, isUploaded) => {
      try {
        setDownloadLoading(true);
        let documentData = null;
        if (isUploaded) {
          const res = await BackendHelper.downloadDocumentById(documentKey);
          documentData = res.data;
        } else {
          const res = await BackendHelper.downloadDocumentByEntityAndIdAndType({
            entityType: documentKey,
            entityId: povId,
            documentKey: entityType,
          });
          documentData = res.data;
        }
        if (!documentData || !documentData.encodedFile) {
          toast.error(`File cannot be found!`);
          return;
        } else {
          FileHelper.downloadBase64File(
            documentData.encodedFile,
            documentData.fileName
          );
        }
      } catch (e) {
        toast.error("Failed to download!");
      } finally {
        setDownloadLoading(false);
      }
    };

    const previewCV = async (documentKey, povId, entityType, isUploaded) => {
      setPreviewLoading(true);
      try {
        let documentData = null;
        if (isUploaded) {
          const res = await BackendHelper.downloadDocumentById(documentKey);
          documentData = res.data;
        } else {
          const res = await BackendHelper.downloadDocumentByEntityAndIdAndType({
            documentKey: entityType,
            entityId: povId,
            entityType: documentKey,
          });

          documentData = res.data;
        }

        const ext = documentData?.fileName.split(".").pop();

        if (!documentData || !documentData.encodedFile) {
          toast.error(`File cannot be found!`);
          return;
        }

        // Handle Microsoft Office documents (docx, doc, xlsx, xls)
        if (["docx", "doc", "xlsx", "xls"].includes(ext)) {
          const formData = new FormData();
          formData.append(
            "docFile",
            FileHelper.base64ToFile(
              documentData?.encodedFile,
              documentData?.fileName
            )
          );

          const convertedData = await BackendHelper.convertMsDocToPdf(formData);
          const docData = convertedData.data;
          const pdfName = docData?.fileName?.split(".")[0] + ".pdf";
          const file = await FileHelper.base64ToFile(
            docData?.encodedFile,
            pdfName
          );

          setFilePreview(file);
          setPreviewModal(true);
          return;
        }

        // Handle PDF files
        if (ext === "pdf") {
          const file = FileHelper.base64ToFile(
            documentData?.encodedFile,
            documentData?.fileName
          );
          setFilePreview(file);
          setPreviewModal(true);
          return;
        }

        // If file extension is not supported
        toast.info("No supported file format found for preview.");
      } catch (e) {
        toast.error("Failed to preview!");
      } finally {
        setPreviewLoading(false);
      }
    };

    return (
      <>
        {tableData.map((data, index) => (
          <tr key={index} style={{ fontSize: "12px" }}>
            <td>{index + 1}.</td>
            <td>{fileData?.[data.type] || "File not available."}</td>
            <td>{data.name}</td>
            <td>
              <div className="d-flex flex-row gap-2">
                <Button
                  onClick={() => {
                    downloadCV(documentKey, povId, data.type);
                  }}
                  disabled={!fileData?.[data.type] || downloadLoading}
                  className="btn btn-sm btn-light"
                >
                  <span className="mdi mdi-download"></span>
                </Button>
                <Button
                  onClick={() => {
                    previewCV(documentKey, povId, data.type);
                  }}
                  disabled={!fileData?.[data.type] || previewLoading}
                  className="btn btn-sm btn-light"
                >
                  <span className="mdi mdi-eye-outline"></span>
                </Button>
              </div>
            </td>
          </tr>
        ))}
        {otherUploadedFiles?.map((data, index) => (
          <tr key={index} style={{ fontSize: "11px" }}>
            <td>{tableData?.length + index + 1}.</td>
            <td>{JSON.parse(data?.submissionData)?.file}</td>
            <td>{data?.title ?? JSON.parse(data?.submissionData)?.title}</td>
            <td>
              <div className="d-flex flex-row gap-2">
                <Button
                  onClick={() => {
                    downloadCV(data?.id, null, null, true);
                  }}
                  className="btn btn-sm btn-light"
                  disabled={downloadLoading}
                >
                  <span className="mdi mdi-download"></span>
                </Button>
                <Button
                  onClick={() => {
                    previewCV(data?.id, null, null, true);
                  }}
                  disabled={previewLoading}
                  className="btn btn-sm btn-light"
                >
                  <span className="mdi mdi-eye-outline"></span>
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </>
    );
  };

  return (
    <div>
      <div className="fw-semibold fs-5 mb-3" style={{color: "rgb(10, 86, 174)"}}>Documents & Attachments</div>
      <Table
        style={{
          border: "1px solid lightgray",
          padding: "15px",
          borderRadius: "8px",
          overflow: "hidden",
          marginBottom: "15px",
          boxShadow: "rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px",
        }}
      >
        <thead className="table-light" style={{ borderColor: "#d3d3d3" }}>
          <tr>
            {headers.map((header, index) => (
              <td key={index} style={{ fontSize: "13px", fontWeight: '500' }}>
                {header}
              </td>
            ))}
          </tr>
        </thead>
        <tbody>{generateBodyJsx(candidateId, jobId)}</tbody>
      </Table>
      <Modal
        isOpen={previewModal}
        toggle={() => setPreviewModal(!previewModal)}
        centered
        size="xl"
      >
        <div className="modal-header">Preview Document</div>
        <div className="modal-body">
          <FilePreview file={filePreview} />
        </div>
      </Modal>
    </div>
  );
}

export default PreOfferDocuments;
